import { updateWpUser } from "../../../api/wordpress";
import InputField from "../../fields/input-field/form-input.component";
import { useState, useContext, useEffect, useRef } from "react";
import { UserContext } from "../../../contexts/user.context";
import SuccessModal from "../../modals/success-modal.component";
import { ReactComponent as EditSvg } from "../../../assets/vectors/edit.svg";
import image from "../../../assets/images/user.png";
import { useNavigate } from "react-router-dom";

const defaultErrorObject = {
  code: "",
  status: false,
  message: "",
};

const EditProfileForm = () => {
  const { persistUpdatedUser, accessToken, user, fsUser, subAccounts, subaccount} = useContext(UserContext);
  const [displayPhoto, setDisplayPhoto] = useState(user?.profile_photo || image);
  const navigate = useNavigate();
  const defaultFormFields = {
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    user_email: user?.user_email || "",
    phone_number: user?.phone_number || "",
    bio: user?.bio || "",
    bank_name: subAccounts?.subaccount?.settlement_bank || subaccount?.subaccount.settlement_bank || "",
    bank_code: subAccounts?.bank_code || subaccount?.bank_code || "",
    account_number: subAccounts?.account_number || subaccount?.account_number || "",
    account_name: subAccounts?.account_name || subaccount?.account_name || "",
  };

  const [formFields, setFormFields] = useState(defaultFormFields);
  const {
    first_name,
    last_name,
    user_email,
    phone_number,
    bank_name,
    bank_code,
    account_number,
    account_name,
    bio,
  } = formFields;

  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [errorObj, setLoginError] = useState(defaultErrorObject);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);

  // account name resolution state
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");
  const resolveTimer = useRef(null);

  useEffect(()=>{
   setFormFields(defaultFormFields)
  },[subAccounts])
  
  // fetch banks on mount
  useEffect(() => {
    let mounted = true;
    async function fetchBanks() {
      setBanksLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/payments/banks`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch banks");
        const data = await res.json();
        if (mounted) setBanks(data || []);
      } catch (err) {
        console.error("Failed to load banks", err);
      } finally {
        if (mounted) setBanksLoading(false);
      }
    }
    fetchBanks();
    return () => (mounted = false);
  }, [accessToken]);

  const handleBack = () => {
    const previousRoute = sessionStorage.getItem("previousRoute");
    if (previousRoute) {
      navigate(previousRoute);

      sessionStorage.removeItem("previousRoute");
    } else {
      navigate("/dashboard/property-list");
    }
  };

const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "profile_photo") {
      const file = event.target.files[0];
      const imagePreview = document.querySelector("#profile-image-preview");
      if (file) {
        imagePreview.src = URL.createObjectURL(file);
        setDisplayPhoto(imagePreview.src);
      }
      setFormFields({ ...formFields, [name]: file });
      return;
    }

    if (name === "phone_number") {
      let raw = value.replace(/\D/g, "");

      const isDeleting = value.length < (phone_number || "").length;
      if (isDeleting && raw.length < 3) {
          setFormFields({ ...formFields, [name]: "" });
          return;
      }

      if (!raw) {
          setFormFields({ ...formFields, [name]: "" });
          return;
      }

      if (raw.startsWith("0")) {
          raw = "234" + raw.substring(1);
      } 
      else if (!raw.startsWith("234")) {
          raw = "234" + raw;
      }

      if (raw.length > 13) raw = raw.substring(0, 13);

      setFormFields({ ...formFields, [name]: "+" + raw });
      return;
    }

    setFormFields({ ...formFields, [name]: value });

    if (name === "account_number") {
      setResolveError("");
      setFormFields(prev => ({ ...prev, account_name: "" }));
      if (resolveTimer.current) clearTimeout(resolveTimer.current);
      if(value.length === 10){
        resolveTimer.current = setTimeout(() => {
          attemptResolveAccount(value, bank_code);
        }, 700);
      }
    }

    if (name === "bank_code") {
      setFormFields(prev => ({ ...prev, bank_code: value, bank_name: (banks.find(b=>b.code === value)?.name) || prev.bank_name }));
      setResolveError("");
      setFormFields(prev => ({ ...prev, account_name: "" }));
      if (resolveTimer.current) clearTimeout(resolveTimer.current);
      if (account_number) {
        resolveTimer.current = setTimeout(() => {
          attemptResolveAccount(account_number, value);
        }, 700);
      }
    }
  }
  // Attempt to resolve account name using backend endpoint
  async function attemptResolveAccount(accNumber, bCode) {
    if (!accNumber || !bCode) return;
    setResolving(true);
    setResolveError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/payments/resolve-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
        body: JSON.stringify({ account_number: accNumber, bank_code: bCode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to resolve account");
      }
      const body = await res.json();
      // expected shape: { status: true, data: { account_name: "Full Name" } }
      const resolved = body?.data?.account_name || body?.account_name || "";
      if (resolved) {
        setFormFields(prev => ({ ...prev, account_name: resolved }));
      } else {
        setResolveError("Account name not found");
      }
    } catch (err) {
      console.error("resolve account error", err);
      setResolveError("Failed to resolve account");
    } finally {
      setResolving(false);
    }
  }

  // call backend to create subaccount on Paystack
  async function createSubaccountOnBackend(payload) {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/payments/subaccount`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.message || "Failed to create subaccount");
    }
    return res.json();
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setLoginError(defaultErrorObject);
     if(formFields.phone_number.length < 10){
      setLoginError({
        code: "phone_number",
        message: "Phone number must be at least 10 digits",
        status: true,
      });
      setLoading(false);
      return;
    }
    const formData = new FormData(event.target);
    formData.append("user_email", user_email);
    if (formFields.profile_photo) {
      formData.append("file_key", "profile_photo");
      formData.append("file", formFields.profile_photo);
    }

    try {
      const response = await updateWpUser(accessToken, formData);
      persistUpdatedUser(response.data);

      // after WP update succeeds we attempt to create a subaccount if bank details provided
      if (account_number && (bank_code || bank_name)) {
        try {
          const businessName = `${first_name || ""} ${last_name || ""}`.trim() || user?.displayName || user?.company_name || "Ruumies User";
          const payload = {
            userId: fsUser.uid,
            bank_code: bank_code || null,
            bank_name: bank_name || null,
            account_number,
            account_name: account_name || null,
            business_name: businessName,
          };

          const subResp = await createSubaccountOnBackend(payload);
          console.log("Subaccount created", subResp);
        } catch (err) {
          console.error("Failed to create subaccount:", err);
          setLoginError({ code: "subaccount_error", message: err.message, status: true });
          setModalState(true);
          setLoading(false);
          return;
        }
      }

      setModalState(true);
    } catch (error) {
      setLoading(false);
      const { code, message } = error.response?.data ? error.response.data : error;
      setLoginError({
        code: code,
        message: message,
        status: true,
      });
      setModalState(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="edit-profile_form" className="font-light w-full" onSubmit={handleSubmit}>
      {errorObj.status && (
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
          <svg className="inline flex-shrink-0 mr-3 w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
          </svg>
          <div className="break-words">{errorObj.message}</div>
        </div>
      )}

      <div className="mb-10 flex justify-center">
        <span role="button" tabIndex={0} onClick={() => document.getElementById("profile_photo").click()} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") document.getElementById("profile_photo").click(); }} className="relative inline-block">
          <img id="profile-image-preview" className="p-1 shadow-md cursor-pointer h-[100px] w-[100px] rounded-full object-cover" src={displayPhoto} alt="profile-img" />
          <div className="absolute rounded-full cursor-pointer bg-secondary h-[24px] w-[24px] bottom-0 right-0 flex items-center justify-center">
            <EditSvg className="p-1 svg-icon-white" />
          </div>
        </span>
      </div>

     
      <input id="profile_photo" name="profile_photo" className="hidden" type="file" onChange={handleChange} />
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-6 px-0 md:px-0">
        
        <div className="w-full md:w-1/2">
          <h5 className="text-gray-500 font-bold text-xs mb-4 uppercase tracking-widest border-b pb-2">
            Personal Details
          </h5>
          
          <div className="grid grid-cols-2 gap-3 md:gap-4">
             <InputField label="First Name" type="text" name="first_name" value={first_name} required disabled onChange={handleChange} />
             <InputField label="Last Name" type="text" name="last_name" value={last_name} required disabled onChange={handleChange} />
          </div>

          <InputField label="Email" type="email" name="user_email" value={user_email} disabled required onChange={handleChange} />
          
          <InputField 
            label="Phone Number" 
            type="tel" 
            name="phone_number" 
            required
            value={phone_number} 
            onChange={handleChange} 
            placeholder="+234 800 000 0000"
          />
        </div>

        <div className="w-full md:w-1/2">
           <h5 className="text-gray-500 font-bold text-xs mb-4 uppercase tracking-widest border-b pb-2">
            Payment Information
          </h5>

          <div className="mb-6">
            <InputField label="Bank Name" type="select" name="bank_code" value={bank_code} options={banks} disabled={banksLoading} required onChange={handleChange} />
          </div>

          <div className="mb-6">
            <InputField label="Account Number" type="text" max={10} name="account_number" value={account_number} required placeholder="0000000000" onChange={handleChange} />
            
            <p className="mt-2 text-xs font-medium text-blue-600">
              {resolving ? "Checking..." : resolveError ? <span className="text-red-500">{resolveError}</span> : account_name}
            </p>
          </div>
          
          <input type="hidden" name="account_name" value={account_name || ""} />
        </div>
      </div>

      <div className="mb-8">
         <h5 className="text-gray-500 font-bold text-xs mb-2 uppercase tracking-widest border-b pb-2">Bio</h5>
         <InputField label="" placeholder="Tell us a bit about yourself..." type="textarea" name="bio" required value={bio} onChange={handleChange} />
      </div>

      <div className="field_wrapper mb-5">
        <button type="submit" className="w-full rounded-md text-white p-3 bg-primary hover:bg-blue-900 font-semibold cursor-pointer">
          {loading && (
            <svg role="status" className="inline w-4 h-4 mr-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8824 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
            </svg>
          )}
          Update Profile
        </button>
      </div>

      <SuccessModal
        action={() => {
          setModalState(false);
          handleBack();
        }}
        state={modalState}
        title="Successful Profile Update!"
        description="Your profile has been successfully updated."
      />
    </form>
  );
};

export default EditProfileForm;
